import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { ModuleVisibilityRepository } from '../../application/ModuleVisibilityRepository'
import { appModuleSettingRowSchema } from '../../domain/moduleVisibility'

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

export function createSupabaseModuleVisibilityRepository(
  client: AppSupabaseClient,
): ModuleVisibilityRepository {
  const untypedClient = client as unknown as SupabaseClient
  return {
    async listModules() {
      const response = asResponse(
        await untypedClient
          .from('app_module_settings')
          .select('module_key,label,is_active,updated_at')
          .order('module_key', { ascending: true }),
      )
      if (response.error)
        throw new Error(`APP_MODULE_SETTINGS_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = z.array(appModuleSettingRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('APP_MODULE_SETTINGS_RESPONSE_INVALID')
      return {
        modules: parsed.data.map((row) => ({
          moduleKey: row.module_key,
          label: row.label,
          isActive: row.is_active,
          updatedAt: row.updated_at,
        })),
        meta: { source: 'supabase' as const, retrievedAt: new Date().toISOString() },
      }
    },
    async setModuleActive(command) {
      const response = asResponse(
        await untypedClient.rpc('set_app_module_active_v1', {
          p_module_key: command.moduleKey,
          p_is_active: command.isActive,
        }),
      )
      if (response.error)
        throw new Error(
          response.error.message || `APP_MODULE_SETTINGS_UPDATE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
    },
  }
}
